import "./auth.css";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { routes } from "../../routes";
import {
  trackSignupFormExit,
  trackSignupFormSubmit,
  trackSignupEmailError,
  trackSignupWithGoogle,
  trackSignupFailedError,
  trackSignupWithFacebook,
  trackSignupPasswordError,
  trackSignupRegistrationStart,
  trackRegistrationSuccess,
} from "../../utils/GoogleTagTracking";
import { Modal } from "react-bootstrap";
import Toaster from "../../components/Toast/Toast";
import { useEffect, useState, useRef } from "react";
import { authImages } from "../../utils/globalImages";
import Loading from "../../components/Loader/Loading";
import { useSelector, useDispatch } from "react-redux";
import { toggleLoader } from "../../redux/slices/loaderSlice";
import { clearMessage } from "../../redux/slices/messageSlice";
import { syncUserData } from "../../services/userSync.service";
import { sendUtmData } from "../../api/modules/b2droppie.class";
import { Link, useLocation, useNavigate } from "react-router-dom";
import WebLogoDark from "../../assets/images/logo/signup-logo.png";
import { toggleOnboard } from "../../redux/slices/onBoardingSlice";
import { register, updateUserData } from "../../redux/slices/authSlice";
import { Mail, Lock, Eye, EyeOff, Link as LinkIcon } from "lucide-react";
import BackdropLoader from "../../components/BackdropLoader/BackdropLoader";

const Signup = () => {
  const navigate = useNavigate();
  const isSubmittingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const onboarding = useSelector((state) => state?.onboarding);
  const [error, setError] = useState({ success: "", error: "" });
  const isDarkMode = useSelector((state) => state.theme.darkMode);
  const isOpen = useSelector((state) => state.onboarding.register);
  const isLoading = useSelector((state) => state?.loader?.isLoading);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 641);

  // Swipe to close states
  let lastClick = 0;
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const cardRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const utm = params.get("utm");
  const subId = params.get("sub-id");
  useEffect(() => {
    if (!utm) return;

    const sendData = async () => {
      const res = await sendUtmData({
        code_used: utm,
        sub_id: subId || null,
      });

      if (res?.success !== false) {
        localStorage.setItem("utm_sent", "true");
      }
    };

    sendData();
  }, [utm]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 641);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset drag when modal opens - FIX: should check register instead of login
  useEffect(() => {
    if (onboarding?.register) {
      setDragY(0);
      setIsDragging(false);
    }
  }, [onboarding?.register]);

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  useEffect(() => {
    if (isOpen) {
      trackSignupRegistrationStart();
    }
  }, [isOpen]);

  // Swipe handlers
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isMobile || !isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;

    // Only allow dragging down
    if (diff > 0) {
      setDragY(diff);
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    setIsDragging(false);

    // If dragged more than 150px, close the modal
    if (dragY > 150) {
      trackSignupFormExit();
      dispatch(toggleOnboard());
    }
    setDragY(0);
  };

  const initialValues = {
    email: "",
    password: "",
    referral_code: "",
    is_terms_accepted: false,
  };

  const SignupSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email address").required("Required"),

    password: Yup.string()
      .required("Password is required")
      .min(8, "Password is too short - should be 8 chars minimum"),

    referral_code: Yup.string()
      .trim()
      .min(3, "Referral code is too short")
      .max(30, "Referral code is too long")
      .nullable(),

    is_terms_accepted: Yup.boolean()
      .oneOf([true], "You must accept the Terms and Conditions")
      .required("Required"),
  });

  const handleSubmit = async (values) => {
    const now = Date.now();
    if (now - lastClick < 2000) return;
    lastClick = now;

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    trackSignupFormSubmit();

    try {
      await new Promise((resolve, reject) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(process.env.REACT_APP_GOOGLE_RE_CAPTCHA_API_KEY, {
              action: "signup",
            })
            .then(resolve)
            .catch(reject);
        });
      }).then(async (token) => {
        const payload = {
          ...values,
          referral_code: values.referral_code?.trim() || null,
          recaptcha_token: token,
          sub_id: subId || null,
        };

        dispatch(toggleLoader({ isLoading: true }));

        const response = await dispatch(register(payload)).unwrap();

        setError((prev) => ({
          ...prev,
          success: response?.message || "Signup successful!",
          error: "",
        }));

        if (response?.user) {
          trackRegistrationSuccess(response?.user?.id);

          try {
            const updated = await dispatch(syncUserData()).unwrap();
            dispatch(updateUserData(updated));
          } catch (err) {
            console.error("User sync failed:", err);
          }

          dispatch(toggleOnboard({ register: false }));

          const from = location.state?.from?.pathname || routes.HOME;

          navigate(from, { replace: true });
        }
      });
    } catch (error) {
      trackSignupFailedError();

      let errorMessage = "Signup failed";

      if (error?.message && typeof error.message === "object") {
        const firstKey = Object.keys(error.message)[0];
        if (firstKey && Array.isArray(error.message[firstKey])) {
          errorMessage = error.message[firstKey][0];
        }
      } else if (typeof error?.message === "string") {
        errorMessage = error.message;
      }

      setError({
        success: "",
        error: errorMessage,
      });

      console.error("Register API error:", error);
    } finally {
      dispatch(toggleLoader({ isLoading: false }));
      isSubmittingRef.current = false;
      setTimeout(() => {
        navigate(`${routes.ACCOUNT}/${routes.ACCOUNT_FREEBOXES}`);
      }, 500);
    }
  };

  const externalLogin = (link) => {
    setLoading(true);
    const currentOrigin = window.location.origin;

    if (currentOrigin === process.env.REACT_APP_FRONTEND_STAGING_URL) {
      window.open(
        `${process.env.REACT_APP_STAGING_URL}/auth/${link}/redirect`,
        "_self",
      );
    } else if (
      currentOrigin === process.env.REACT_APP_FRONTEND_PRODUCTION_URL
    ) {
      window.open(
        `${process.env.REACT_APP_PRODUCTION_URL}/auth/${link}/redirect`,
        "_self",
      );
    } else if (currentOrigin === process.env.REACT_APP_FRONTEND_LOCAL_URL) {
      window.open(
        `${process.env.REACT_APP_STAGING_URL}/auth/${link}/redirect`,
        "_self",
      );
    }
  };

  const backdropOpacity = isMobile ? Math.max(0, 1 - dragY / 300) : 1;

  return (
    <>
      <Toaster message={error} />

      <Modal
        size="md"
        centered={!isMobile}
        show={onboarding?.register}
        className={`modern-auth-modal ${isMobile ? "mobile-bottom-sheet" : ""}`}
        dialogClassName="modern-modal-dialog"
        onHide={() => {
          trackSignupFormExit();
          dispatch(toggleOnboard());
        }}
        backdrop={true}
        animation={false}
        style={isMobile ? { "--backdrop-opacity": backdropOpacity } : {}}
      >
        <Modal.Body className="modern-modal-body p-0 m-0">
          <div
            ref={cardRef}
            className={`modern-auth-card ${
              isDarkMode ? "dark-mode" : "light-mode"
            }`}
            style={
              isMobile
                ? {
                    transform: `translateY(${dragY}px)`,
                    transition: isDragging ? "none" : "transform 0.3s ease-out",
                  }
                : {}
            }
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Swipe Handle - Only on mobile */}
            {isMobile && (
              <div className="swipe-handle-container">
                <div className="swipe-handle" />
              </div>
            )}
            <div className="d-center">
              <img
                height={104}
                alt="Droppie Logo"
                src={isDarkMode && WebLogoDark}
              />
            </div>

            <div className="auth-buttons-container text-nowrap">
              <button
                type="submit"
                disabled={isLoading}
                className="modern-login-btn-google"
                onClick={() => {
                  trackSignupWithGoogle();
                  externalLogin("google");
                }}
              >
                <span className="d-center">
                  <img
                    height={24}
                    width={24.49}
                    alt="Google logo"
                    src={authImages.googleColored}
                  />
                  <p className="ms-2 mb-0">Sign up with Google</p>
                </span>
              </button>

              <button
                type="submit"
                disabled={isLoading}
                onClick={() => {
                  trackSignupWithFacebook();
                  externalLogin("facebook");
                }}
                className="modern-login-btn-facebook"
              >
                <span className="d-center">
                  <img
                    height={24}
                    width={24.49}
                    alt="facebook logo"
                    src={authImages.facebookBlue}
                  />
                  <p className="ms-2 mb-0">Sign up with Facebook</p>
                </span>
              </button>
            </div>

            {/* Divider */}
            <div className="modern-divider">
              <div className="modern-divider-line text-purple-taupe"></div>
              <span
                className={`modern-divider-text ${
                  isDarkMode ? "text-dark-charcoal-gray" : "text-silver-gray"
                }`}
              >
                OR
              </span>
              <div className="modern-divider-line text-purple-taupe"></div>
            </div>

            <Formik
              initialValues={{ ...initialValues, referral_code: utm || "" }}
              validationSchema={SignupSchema}
              onSubmit={async (values, { setSubmitting }) => {
                handleSubmit(values);
              }}
            >
              {({ values, errors, touched, handleChange, handleSubmit }) => (
                <Form>
                  {/* Email Input */}
                  <div className="modern-form-group">
                    <label className="modern-label">Email</label>
                    <div className="modern-input-wrapper">
                      <Mail className="modern-input-icon" />
                      <input
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        className="modern-input"
                        placeholder="e.g. john.smith@example.com"
                      />
                    </div>
                    {errors.email && touched.email && (
                      <span className="error">{errors.email}</span>
                    )}
                  </div>

                  {/* Password Input */}
                  <div className="modern-form-group mt-2">
                    <label className="modern-label">Password</label>
                    <div className="modern-input-wrapper">
                      <Lock className="modern-input-icon" />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="••••••••"
                        onChange={handleChange}
                        value={values.password}
                        className="modern-input modern-input-password"
                      />
                      <span
                        className="modern-eye-icon"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </span>
                    </div>
                    {errors.password && touched.password && (
                      <span className="error">{errors.password}</span>
                    )}
                  </div>

                  {/* Referral Input */}
                  <div className="modern-form-group mt-2">
                    <label className="modern-label">
                      Promo Code (Optional)
                    </label>

                    <div className="modern-input-wrapper">
                      <LinkIcon className="modern-input-icon" />

                      <input
                        type="text"
                        name="referral_code"
                        value={values.referral_code}
                        onChange={handleChange}
                        className="modern-input"
                        placeholder="Enter referral code (if any)"
                        autoComplete="off"
                      />
                    </div>

                    {errors.referral_code && touched.referral_code && (
                      <span className="error">{errors.referral_code}</span>
                    )}
                  </div>

                  {/* Terms & Conditions */}
                  <div className="mb-3 mt-2">
                    <label className="signup-terms-checkbox">
                      <input
                        type="checkbox"
                        onChange={handleChange}
                        name="is_terms_accepted"
                        checked={values.is_terms_accepted}
                      />
                      <span className="custom-checkbox"></span>
                      <p
                        className="mb-0 text-14px text-fw-400"
                        style={{ marginLeft: 15, fontSize: 14 }}
                      >
                        <span className="text-cream-beige">
                          I agree to the{" "}
                        </span>
                        <Link
                          to={routes.TERMS}
                          onClick={() => dispatch(toggleOnboard())}
                          className="text-periwinkle-blue cursor-pointer no-underline"
                        >
                          Terms and Conditions
                        </Link>
                      </p>
                    </label>

                    {errors.is_terms_accepted && touched.is_terms_accepted && (
                      <span className="error">{errors.is_terms_accepted}</span>
                    )}
                  </div>

                  {/* Sign Up Button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    onClick={() => {
                      if (errors.email) trackSignupEmailError();
                      if (errors.password) trackSignupPasswordError();
                    }}
                    className={`d-center ${
                      isDarkMode
                        ? "filled-medium-deep-yellow-btn-dark"
                        : "filled-medium-deep-purple-btn-light"
                    }`}
                  >
                    {isLoading ? <Loading /> : "Sign Up"}
                  </button>
                </Form>
              )}
            </Formik>

            {/* Login Button */}
            <div className="d-center mt-4 text-14px">
              <span style={{ fontWeight: "400" }} className="text-medium-gray">
                Already have an account?{" "}
              </span>{" "}
              <span
                onClick={() => dispatch(toggleOnboard({ login: true }))}
                className={`ms-1 ${
                  isDarkMode ? "text-periwinkle-blue" : "text-deep-purple"
                } text-fw-400 cursor-pointer text-underline-hover`}
              >
                Log In
              </span>
            </div>
            {isMobile && (
              <div className="swipe-handle-container-bottom border-0 mt-0">
                <div className="swipe-handle-bottom" />
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
      <BackdropLoader show={loading} />
    </>
  );
};

export default Signup;
